import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import logo from '../assets/logo.png';
import '../styles/Auth.css';
import '../styles/Register.css';

// ── Static config ─────────────────────────────────────────────────────────────
const LOC_UI = {
    detecting: {
        icon: '📍',
        text: 'Detecting your location…',
    },
    captured: {
        icon: '✅',
        text: 'Location captured',
    },
    failed: {
        icon: '❌',
        text: 'Location required — please enable location services',
    },
};

const Spinner = () => <span className="btn-spinner" aria-hidden="true" />;

// ── Component ─────────────────────────────────────────────────────────────────
const Register = () => {
    const [name,           setName]           = useState('');
    const [email,          setEmail]          = useState('');
    const [password,       setPassword]       = useState('');
    const [errors,         setErrors]         = useState({ name: '', email: '', password: '', general: '' });
    const [loading,        setLoading]        = useState(false);
    const [location,       setLocation]       = useState(null);
    const [locationStatus, setLocationStatus] = useState('detecting');

    const { refreshUser } = useContext(AuthContext);
    const navigate = useNavigate();

    // ── Geolocation on mount ──────────────────────────────────────────────────
    // Runs immediately so the status indicator updates before the user
    // fills in a single field — no surprise block on submit.
    useEffect(() => {
        if (!navigator.geolocation) {
            setLocationStatus('failed');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLocation({
                    type: 'Point',
                    coordinates: [pos.coords.longitude, pos.coords.latitude],
                });
                setLocationStatus('captured');
            },
            () => setLocationStatus('failed'),
            { timeout: 10000 }
        );
    }, []);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const clearField = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validate = () => {
        const next = { name: '', email: '', password: '', general: '' };
        let valid  = true;

        if (!name.trim() || name.trim().length < 2) {
            next.name = name.trim() ? 'Name must be at least 2 characters' : 'Name is required';
            valid = false;
        }

        if (!email.trim()) {
            next.email = 'Email is required';
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            next.email = 'Please enter a valid email address';
            valid = false;
        }

        if (!password) {
            next.password = 'Password is required';
            valid = false;
        } else if (password.length < 6) {
            next.password = 'Password must be at least 6 characters';
            valid = false;
        }

        if (locationStatus !== 'captured') {
            next.general = locationStatus === 'detecting'
                ? 'Location is still being detected — please wait a moment'
                : 'Location is required. Please enable location services and refresh the page.';
            valid = false;
        }

        setErrors(next);
        return valid;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setErrors({ name: '', email: '', password: '', general: '' });

        try {
            const response = await api.post('/auth/register', {
                name, email, password, location,
            });

            // Store token, then hydrate AuthContext via refreshUser —
            // avoids a second login API call with the raw password.
            localStorage.setItem('token', response.data.accessToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;

            await refreshUser();
            navigate('/feed');

        } catch (error) {
            const next = { name: '', email: '', password: '', general: '' };

            if (error.response?.data?.errors) {
                // Express-validator returns an array; each item has param (v6) or path (v7)
                error.response.data.errors.forEach(err => {
                    const field = err.param || err.path;
                    if (field in next) next[field] = err.msg;
                    else              next.general  = err.msg;
                });
            } else {
                next.general = error.response?.data?.message
                    || 'Registration failed. Please try again.';
            }

            setErrors(next);
        } finally {
            setLoading(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="auth-page">

            {/* ── Left branding panel ── */}
            <div className="auth-left">
                <div className="auth-left-inner">
                    <img src={logo} alt="Comrades" className="auth-logo" />
                    <h1 className="auth-tagline">Join your local community</h1>
                    {/* Pulsing pin hints at the geolocation step */}
                    <div className="auth-loc-pin" aria-hidden="true">📍</div>
                    <ul className="auth-bullets">
                        <li>
                            <span className="auth-bullet-icon">🏘️</span>
                            Discover what's happening in your neighbourhood
                        </li>
                        <li>
                            <span className="auth-bullet-icon">🔔</span>
                            Get real-time alerts for nearby events
                        </li>
                        <li>
                            <span className="auth-bullet-icon">⭐</span>
                            Build your local reputation over time
                        </li>
                    </ul>
                </div>
            </div>

            {/* ── Right form panel ── */}
            <div className="auth-right">
                <div className="auth-form-wrap">
                    <h2 className="auth-heading">Create your account</h2>
                    <p className="auth-sub">Join Comrades and connect with your neighbours</p>

                    <form onSubmit={handleSubmit} noValidate>

                        <div className="field">
                            <label htmlFor="reg-name">Name</label>
                            <input
                                id="reg-name"
                                type="text"
                                value={name}
                                onChange={e => { setName(e.target.value); clearField('name'); }}
                                placeholder="Your full name"
                                disabled={loading}
                                className={errors.name ? 'input--error' : ''}
                                autoComplete="name"
                            />
                            {errors.name && (
                                <span className="field-error" role="alert">{errors.name}</span>
                            )}
                        </div>

                        <div className="field">
                            <label htmlFor="reg-email">Email</label>
                            <input
                                id="reg-email"
                                type="email"
                                value={email}
                                onChange={e => { setEmail(e.target.value); clearField('email'); }}
                                placeholder="you@example.com"
                                disabled={loading}
                                className={errors.email ? 'input--error' : ''}
                                autoComplete="email"
                            />
                            {errors.email && (
                                <span className="field-error" role="alert">{errors.email}</span>
                            )}
                        </div>

                        <div className="field">
                            <label htmlFor="reg-password">Password</label>
                            <input
                                id="reg-password"
                                type="password"
                                value={password}
                                onChange={e => { setPassword(e.target.value); clearField('password'); }}
                                placeholder="At least 6 characters"
                                disabled={loading}
                                className={errors.password ? 'input--error' : ''}
                                autoComplete="new-password"
                            />
                            {errors.password && (
                                <span className="field-error" role="alert">{errors.password}</span>
                            )}
                        </div>

                        {/* Location status indicator — always visible, updates automatically */}
                        <div className={`loc-status loc-status--${locationStatus}`}>
                            <span className="loc-icon">{LOC_UI[locationStatus].icon}</span>
                            <span className="loc-text">{LOC_UI[locationStatus].text}</span>
                        </div>

                        {errors.general && (
                            <div className="form-error" role="alert">{errors.general}</div>
                        )}

                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading
                                ? <><Spinner /> Creating account…</>
                                : 'Create account'}
                        </button>

                    </form>

                    <p className="auth-switch">
                        Already have an account?{' '}
                        <Link to="/login">Sign in here</Link>
                    </p>
                </div>
            </div>

        </div>
    );
};

export default Register;
